import { useEffect, useMemo, useState } from 'react'
import {
  faCirclePlus,
  faEdit,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FunctionModal } from './FunctionModal'
import { useContextStateManagement } from '../context/ContextApi'
import { Modal } from 'react-bootstrap'
import { logout } from '../features/userSlice'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { currentAPIAndMemberList } from '../Variables'
import './ApiIntegration.css'

export const ApiIntegration = () => {
  const {
    PracticeId,
    token,
    fetchUserRecord,
    deleteRequest,
    setToken,
    setActiveTab,
    setUserType,
    activeTab,
  } = useContextStateManagement()
  const [isTokenExpired, setIsTokenExpired] = useState(false)
  const [modalShow, setModalShow] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(false)
  const [apiFunctions, setApiFunctions] = useState([])
  const [editFunctionData, setEditFunctionData] = useState(null)
  const [functionIdsToDelete, setFunctionIdsToDelete] = useState([])
  const [selectedFunctionIds, setSelectedFunctionIds] = useState([])
  const [initialFunctionType, setInitialFunctionType] = useState('apiFunction')
  const [loading, setLoading] = useState(false)
  const [isFunction, setIsFunction] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [selectedFunctionTypeFilter, setSelectedFunctionTypeFilter] = useState('all')
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const functionIdSet = new Set(apiFunctions.map((func) => func.Id))
  const filteredFunctions =
    selectedFunctionTypeFilter === 'all'
      ? apiFunctions
      : apiFunctions.filter((func) => func.FunctionType === selectedFunctionTypeFilter)
  const allVisibleFunctionIds = filteredFunctions.map((func) => func.Id)

  const selectedCount = selectedFunctionIds.length
  const isAllSelected =
    allVisibleFunctionIds.length > 0 &&
    allVisibleFunctionIds.every((id) => selectedFunctionIds.includes(id))

  const functionTypeCards = useMemo(
    () => [
      {
        key: 'apiFunction',
        title: 'API Function',
        description: 'Connect a new external function for the current workflow.',
      },
      {
        key: 'warm_transfer',
        title: 'Warm Transfer',
        description: 'Configure a warm handoff flow for assisted transfers.',
      },
      {
        key: 'cold_transfer',
        title: 'Cold Transfer',
        description: 'Create a cold transfer function for direct call routing.',
      },
    ],
    [],
  )

  const fetchApiHandle = async () => {
    const { url } = currentAPIAndMemberList(activeTab, PracticeId)
    const userData = await fetchUserRecord(token, url)

    if (userData === 'Token is expired, login again') {
      setIsTokenExpired(true)
      setIsFunction('')
      return
    }

    if (userData && userData.functions && userData.functions.length === 0) {
      setApiFunctions([])
      setSelectedFunctionIds([])
      setIsFunction('No function found.')
    } else if (userData && userData.functions) {
      setApiFunctions(userData.functions)
      setSelectedFunctionIds((currentIds) =>
        currentIds.filter((id) =>
          userData.functions.some((func) => func.Id === id),
        ),
      )
      setIsFunction('')
    }
  }

  const handleEditFunction = (id) => {
    const getEditFunction = apiFunctions.find((func) => func.Id === id)
    if (getEditFunction) {
      setEditFunctionData(getEditFunction)
      setModalShow(true)
    }
  }

  const handleOpenFunctionModal = (functionType) => {
    setEditFunctionData(null)
    setInitialFunctionType(functionType)
    setModalShow(true)
  }

  const openDeleteModal = (ids) => {
    const normalizedIds = ids.filter((id) => functionIdSet.has(id))
    if (normalizedIds.length === 0) {
      return
    }

    setFunctionIdsToDelete(normalizedIds)
    setSuccessMessage('')
    setConfirmationModal(true)
  }

  const closeDeleteModal = () => {
    if (loading) {
      return
    }

    setConfirmationModal(false)
    setFunctionIdsToDelete([])
    setSuccessMessage('')
  }

  const handleDeleteFunction = async () => {
    if (functionIdsToDelete.length === 0) {
      return
    }

    setLoading(true)

    const userData = await deleteRequest(token, '/api/deleteFunction', {
      practiceId: PracticeId,
      functionIds: functionIdsToDelete.join(','),
    })

    if (userData === 'Token is expired, login again') {
      setIsTokenExpired(true)
    } else if (userData && userData.success === true) {
      setSuccessMessage(
        userData.message ||
          (functionIdsToDelete.length === 1
            ? 'The function was deleted successfully.'
            : `${functionIdsToDelete.length} functions were deleted successfully.`),
      )
      setSelectedFunctionIds((currentIds) =>
        currentIds.filter((id) => !functionIdsToDelete.includes(id)),
      )
      await fetchApiHandle()
    }

    setLoading(false)
  }

  const handleToggleFunctionSelection = (functionId) => {
    setSelectedFunctionIds((currentIds) =>
      currentIds.includes(functionId)
        ? currentIds.filter((id) => id !== functionId)
        : [...currentIds, functionId],
    )
  }

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedFunctionIds([])
      return
    }

    setSelectedFunctionIds(allVisibleFunctionIds)
  }

  useEffect(() => {
    fetchApiHandle()
    // fetchApiHandle depends on current tab, practice, and token state above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, token, activeTab])

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  return (
    <div className="api-integration-shell">
      <div className='my-3 d-flex flex-wrap align-items-center gap-2'>
        <div className='me-3 fs-3'>Functions</div>
        {selectedCount > 0 && (
          <button
            type='button'
            className='simple-btn'
            onClick={() => openDeleteModal(selectedFunctionIds)}
          >
            <FontAwesomeIcon icon={faTrash} className='me-2 color-red' />
            Delete Selected ({selectedCount})
          </button>
        )}
        <FunctionModal
          show={modalShow}
          handleClose={() => {
            setModalShow(false)
            setEditFunctionData(null)
            setInitialFunctionType('apiFunction')
          }}
          memberType={currentAPIAndMemberList(activeTab, PracticeId)?.memberList}
          fetchApiHandle={fetchApiHandle}
          editFunctionData={editFunctionData}
          initialFunctionType={initialFunctionType}
        />
      </div>

      <div className="api-integration-body">
        <div className="api-integration-mini-sidebar" role="tablist" aria-label="Function type filter">
          <button
            type="button"
            role="tab"
            aria-selected={selectedFunctionTypeFilter === 'all'}
            className={`api-integration-mini-sidebar__item ${
              selectedFunctionTypeFilter === 'all' ? 'api-integration-mini-sidebar__item--active' : ''
            }`}
            onClick={() => setSelectedFunctionTypeFilter('all')}
          >
            All functions
          </button>
          {functionTypeCards.map((card) => (
            <button
              key={card.key}
              type="button"
              role="tab"
              aria-selected={selectedFunctionTypeFilter === card.key}
              className={`api-integration-mini-sidebar__item ${
                selectedFunctionTypeFilter === card.key
                  ? 'api-integration-mini-sidebar__item--active'
                  : ''
              }`}
              onClick={() => setSelectedFunctionTypeFilter(card.key)}
            >
              {card.title}
            </button>
          ))}
        </div>

        <div className="api-integration-content">
          <div className="api-integration-cards">
            {functionTypeCards.map((card) => (
              <button
                key={card.key}
                type="button"
                className="api-integration-card"
                onClick={() => handleOpenFunctionModal(card.key)}
              >
                <div className="api-integration-card__copy">
                  <span className="api-integration-card__kicker">{card.title}</span>
                  <span className="api-integration-card__description">
                    {card.description}
                  </span>
                </div>
                <span className="api-integration-card__plus" aria-hidden="true">
                  <FontAwesomeIcon icon={faCirclePlus} />
                </span>
              </button>
            ))}
          </div>

          {selectedCount > 0 && (
            <div className='mb-3 small text-light-emphasis'>
              {selectedCount} function{selectedCount === 1 ? '' : 's'} selected.
            </div>
          )}

          <div className='row'>
            <div className='col-12'>
              <div className='table-responsive'>
                <table className='table align-middle' style={{ color: '#9da7aa' }}>
                  <thead>
                    <tr>
                      <th scope='col' className='col-1'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          checked={isAllSelected}
                          onChange={handleToggleSelectAll}
                          aria-label='Select all functions'
                        />
                      </th>
                      <th scope='col' className='col-1'>
                        #
                      </th>
                      <th scope='col' className='col-4'>
                        Name
                      </th>
                      <th scope='col' className='col-4'>
                        Function type
                      </th>
                      <th scope='col' className='col-2'>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFunctions.length > 0 &&
                      filteredFunctions.map((func, index) => {
                        const isSelected = selectedFunctionIds.includes(func.Id)

                        return (
                          <tr key={func.Id}>
                            <td>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                checked={isSelected}
                                onChange={() =>
                                  handleToggleFunctionSelection(func.Id)
                                }
                                aria-label={`Select function ${func.Name}`}
                              />
                            </td>
                            <td>{index + 1}</td>
                            <td>{func.Name}</td>
                            <td>{func.FunctionType}</td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <span
                                  title='Edit function'
                                  className='cursor-pointer'
                                  onClick={() => handleEditFunction(func.Id)}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </span>
                                <span
                                  title='Delete function'
                                  className='cursor-pointer'
                                  onClick={() => openDeleteModal([func.Id])}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className='ms-3 color-red'
                                  />
                                </span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
                {apiFunctions.length > 0 && filteredFunctions.length === 0 ? (
                  <div className='mt-3 ms-2'>No functions of this type yet.</div>
                ) : (
                  isFunction && <div className='mt-3 ms-2'>{isFunction}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal className='popup' show={confirmationModal} onHide={closeDeleteModal}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            {functionIdsToDelete.length > 1 ? 'Delete Functions' : 'Delete Function'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className='d-flex justify-content-center'>
              <div className='spinner-border text-light' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : successMessage ? (
            <p>{successMessage}</p>
          ) : (
            <p>
              {functionIdsToDelete.length === 1
                ? 'Are you sure you want to permanently delete this function?'
                : `Are you sure you want to permanently delete these ${functionIdsToDelete.length} functions?`}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {successMessage ? (
            <button className='simple-btn me-2' onClick={closeDeleteModal}>
              Close
            </button>
          ) : (
            <>
              <button className='simple-btn me-2' onClick={closeDeleteModal}>
                No
              </button>
              <button className='simple-btn' onClick={handleDeleteFunction}>
                Yes
              </button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  )
}
